/* eslint no-param-reassign: ["error", { "props": true, "ignorePropertyModificationsFor": ["storeDraft"]  }] */
import { type StoreApi, createStore } from 'zustand/vanilla';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/vanilla/shallow';

import { type MasonryEngineNodeStates } from './types/masonry-engine-state.types';
import { useMasonryEngineConfig } from './masonry-engine-config-context';
import {
  type AnyMasonryEngineNode,
  type MasonryEngineNode,
  type MasonryEngineFormFieldNode,
  type BaseFormFieldNodeProperties,
} from './types/masonry-engine-node.types';
import type { MasonryShowModalMessage } from './types/masonry-engine-message.types';

export type MasonryEngineNodeStateFromNode<
  Node extends MasonryEngineNode = MasonryEngineNode,
> = Node['properties'] & MasonryEngineNodeStates;

export type MasonryEngineStateController = {
  /**
   * Bucket for storing miscellaneous instance-level state.
   */
  instance: {
    asyncNodeCacheSeeds: Map<MasonryEngineNode['id'], AnyMasonryEngineNode>;
    modalState: MasonryShowModalMessage['payload'] | undefined;
  };
  /**
   * Node State Map
   */
  nodeStateMap: Map<
    MasonryEngineNode['id'],
    MasonryEngineNodeStateFromNode<AnyMasonryEngineNode>
  >;

  /**
   * field ID-to-node ID map
   */
  formFieldNodesMap: Map<
    BaseFormFieldNodeProperties['formFieldProperties']['formId'],
    MasonryEngineNode['id'][]
  >;
  /**
   * Set any node's state with a callback, which receives the current state and
   * returns what should be the next state.
   */
  setNodeState: <Node extends MasonryEngineNode>(
    nodeId: string,
    producer: (
      state: MasonryEngineNodeStateFromNode<Node>,
    ) => MasonryEngineNodeStateFromNode<Node>,
  ) => void;
  /**
   * Override any node's state.
   */
  setInitialNodeState: <Node extends MasonryEngineNode>(
    nodeId: string,
    state: MasonryEngineNodeStateFromNode<Node>,
  ) => void;
  /**
   * Removes the state of a node from the state controller.
   * @param nodeId - The ID of the node to clear state for
   *
   * @example
   * // Clear the node state
   * stateController.clearNodeState('node-123');
   */
  clearNodeState: (nodeId: MasonryEngineNode['id']) => void;
  /**
   * Getter function to get the node state using node id
   */
  getNodeState: <Node extends MasonryEngineNode>(
    nodeId: string,
  ) => MasonryEngineNodeStateFromNode<Node> | undefined;

  /**
   * Given a form ID, returns the node IDs of all form field nodes that belong to that form,
   * or `undefined` if none have been registered.
   */
  getFormFieldNodesMap: (
    formId: string,
  ) => MasonryEngineNode['id'][] | undefined;
};

/**
 * Function createStateControllerStore is used to create a new zustand store. It returns store instance which
 * can be used to get or set any state of the MasonryEngineStateController.
 */
export const createStateControllerStore = () => {
  /**
   * A store for All Masonry Engine Nodes and Forms states.
   */
  const stateControllerStore = createStore<MasonryEngineStateController>(
    (set, get) => ({
      instance: {
        modalState: undefined,
        asyncNodeCacheSeeds: new Map(),
      },
      nodeStateMap: new Map(),
      formFieldNodesMap: new Map(),
      setNodeState: (
        nodeId,
        producer, // adding A function which can act and prev current state setter like rea
      ) =>
        set((store) => {
          const currentNodeState = store.nodeStateMap.get(nodeId);
          if (!currentNodeState) {
            throw new Error(
              'Trying to set the state of a node whose state was not yet initialized!',
            );
          }

          const newNodeStateMap = new Map(store.nodeStateMap);
          newNodeStateMap.set(nodeId, producer(currentNodeState));

          // DevTools pulse hook (immediate, no diffing/batching)
          if (typeof window !== 'undefined') {
            /* eslint-disable-next-line no-underscore-dangle */
            const devtools = window.__MASONRY_DEVTOOLS__;
            // Optional chaining ensures zero runtime impact if hook absent or throws
            try {
              devtools?.onNodeStateMutation?.({ nodeId, t: performance.now() });
            } catch {
              // Swallow errors to avoid impacting app runtime
            }
          }

          return { nodeStateMap: newNodeStateMap };
        }),
      setInitialNodeState: (nodeId, newNodeState) =>
        set((store) => {
          const newNodeStateMap = new Map(store.nodeStateMap);
          newNodeStateMap.set(nodeId, { ...newNodeState });

          let newFormFieldNodesMap = store.formFieldNodesMap;

          // If the node is a form field node, add it to the form field node map when the node state is initialized
          if ('formFieldProperties' in newNodeState) {
            const {
              formFieldProperties: { formId },
            } =
              newNodeState as MasonryEngineNodeStateFromNode<MasonryEngineFormFieldNode>;
            newFormFieldNodesMap = new Map(store.formFieldNodesMap);
            newFormFieldNodesMap.set(formId, [
              ...(newFormFieldNodesMap.get(formId) || []),
              nodeId,
            ]);
          }

          return {
            nodeStateMap: newNodeStateMap,
            formFieldNodesMap: newFormFieldNodesMap,
          };
        }),
      clearNodeState: (nodeId) =>
        set((store) => {
          const newNodeStateMap = new Map(store.nodeStateMap);
          const nodeState = newNodeStateMap.get(nodeId);

          newNodeStateMap.delete(nodeId);

          let newFormFieldNodesMap = store.formFieldNodesMap;

          // If the node is a form field node, remove it from the form field node map when the node state is cleared
          if (nodeState && 'formFieldProperties' in nodeState) {
            const {
              formFieldProperties: { formId },
            } =
              nodeState as MasonryEngineNodeStateFromNode<MasonryEngineFormFieldNode>;
            newFormFieldNodesMap = new Map(store.formFieldNodesMap);
            const formFieldNodes = newFormFieldNodesMap.get(formId) || [];
            newFormFieldNodesMap.set(
              formId,
              formFieldNodes.filter((id) => id !== nodeId),
            );
          }

          return {
            nodeStateMap: newNodeStateMap,
            formFieldNodesMap: newFormFieldNodesMap,
          };
        }),
      getNodeState: (nodeId) => {
        const store = get();
        return store.nodeStateMap.get(nodeId);
      },
      getFormFieldNodesMap: (formId: string) => {
        const store = get();
        return store.formFieldNodesMap.get(formId);
      },
    }),
  );

  return stateControllerStore;
};

export type MasonryEngineStateControllerStore =
  StoreApi<MasonryEngineStateController>;

/**
 * React hook which converts zustand vanilla store into a zustand react store
 * using useStore hook from zustand
 */
export const useStateControllerStore = <T>(
  selector: (state: MasonryEngineStateController) => T,
): T => {
  const { stateControllerStore } = useMasonryEngineConfig();
  return useStoreWithEqualityFn(stateControllerStore, selector, shallow);
};

export const INITIAL_COMMON_STATE: MasonryEngineNodeStates = {
  isHidden: false,
};

/**
 * given a section from a `MasonryEngineNode`, returns the state of all nodes
 * within that section. This is useful when a renderer needs to access the state
 * of any of its direct child nodes.
 */
export const useSectionNodeStates = <Node extends MasonryEngineNode>(
  sectionNodes: Node[],
) =>
  useStateControllerStore((state) =>
    sectionNodes.map((node) => state.getNodeState<Node>(node.id)),
  );
